import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, Navigation, Loader2 } from 'lucide-react';

/**
 * LiveMapGL — Mapbox GL JS v3 + Standard v5 renderer.
 *
 * Drop-in replacement for the Leaflet-based LiveMap. Same props, but with:
 *   - Vector tiles (way faster on mobile than OSM raster)
 *   - Standard v5 style with 3D buildings and dynamic lighting
 *   - Auto day/night light preset based on the user's clock
 *   - Traffic-aware route polyline via Mapbox Directions API
 *   - Smooth driver-marker easing (no manual lerp needed)
 *   - Native pinch/rotate/pitch gestures
 *
 * Falls back gracefully (renders a friendly warning) if the Mapbox token is
 * missing at runtime — no crash, no white screen.
 */

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
// Default map style. `streets-v12` is the classic Mapbox style — roads,
// labels, buildings all render reliably with any Mapbox token. Standard v5
// looked flat/empty on some production tokens (missing scope for the newer
// vector tileset). Overridable via env for a Studio-published custom style.
const MAPBOX_STYLE_URL = process.env.REACT_APP_MAPBOX_STYLE_URL || 'mapbox://styles/mapbox/navigation-day-v1';
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

// Pick day/night light preset based on the user's local clock so the map
// matches the vibe outside their window. Users can override later if we
// want a manual dark-mode toggle.
function computeLightPreset() {
  const h = new Date().getHours();
  if (h >= 5 && h < 7) return 'dawn';
  if (h >= 7 && h < 18) return 'day';
  if (h >= 18 && h < 21) return 'dusk';
  return 'night';
}

// Convert app-shape {lat, lng} → Mapbox [lng, lat]
function toLngLat(loc) {
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null;
  return [loc.lng, loc.lat];
}

export default function LiveMapGL({
  driverLocation,
  pickupLocation,
  dropoffLocation,
  customerLocation,
  showRoute = false,
  height = '300px',
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const driverAnimRef = useRef(null); // in-flight RAF id for driver marker easing
  const routeLoadedRef = useRef(false);
  // Debounce state for the Directions API — we only re-fetch when the
  // driver has moved meaningfully or too much time has passed since the
  // last successful fetch. Prevents a 60x/min live-tracking loop from
  // hammering Mapbox and blowing through free-tier quota.
  const lastRouteFetchRef = useRef({ key: '', at: 0, driverAnchor: null });
  const [mapReady, setMapReady] = useState(false);
  const [tokenMissing] = useState(!MAPBOX_TOKEN);
  // Surface real Mapbox errors (invalid token, style URL 401/403, quota
  // exceeded, referrer-blocked domain, etc.) so we never render a silent
  // blank-white canvas in production again. Previous behaviour swallowed
  // every failure inside the mapboxgl runtime.
  const [mapError, setMapError] = useState(null);

  // -------- Init the map once --------
  useEffect(() => {
    if (!MAPBOX_TOKEN || !mapRef.current || mapInstanceRef.current) return;

    // Reasonable default center — Reading, PA (the platform's flagship city).
    // Overridden immediately by fitBounds once we have pickup/dropoff/driver.
    const initialCenter = toLngLat(pickupLocation)
      || toLngLat(driverLocation)
      || [-75.9268, 40.3356];

    const map = new mapboxgl.Map({
      container: mapRef.current,
      style: MAPBOX_STYLE_URL,
      center: initialCenter,
      zoom: 13,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      // Cooperative gestures on desktop so scroll doesn't hijack the page.
      // Mobile still uses pinch-to-zoom + drag as usual.
      cooperativeGestures: false,
    });
    mapInstanceRef.current = map;

    // Add attribution in a compact position that doesn't cover trip cards.
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
    // A single nav control (compass + zoom) — hidden on very small viewports
    // via CSS so it doesn't fight the trip UI for space.
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }), 'top-right');

    // Surface fatal Mapbox errors so we never render a silent blank-white
    // canvas again. Style-URL 401/403 (invalid token, referrer-blocked
    // domain, plan-not-enabled style) and tile 429 (quota exceeded) both
    // fire through this handler.
    map.on('error', (e) => {
      const err = e && e.error;
      if (!err) return;
      const status = err.status || err.statusCode;
      const url = err.url || '';
      let msg = err.message || 'Map failed to load';
      if (status === 401 || status === 403) {
        msg = 'Mapbox token was rejected — check the token or the allowed URLs list in your Mapbox dashboard.';
      } else if (status === 429) {
        msg = 'Mapbox monthly quota exceeded — check your Mapbox billing dashboard.';
      } else if (/tiles\.mapbox\.com|api\.mapbox\.com/.test(url) && !status) {
        msg = 'Mapbox tiles could not load — likely a missing REACT_APP_MAPBOX_TOKEN on this deployment or a domain restriction on your token.';
      }
      setMapError((prev) => prev || msg);
    });

    map.on('style.load', () => {
      // Standard v5 config knobs — only apply when the loaded style
      // actually exposes them (Standard-derived styles). streets-v12 and
      // most Studio styles ignore these; wrapped in try/catch so a
      // non-Standard style doesn't spew console errors.
      try {
        if (MAPBOX_STYLE_URL.includes('standard')) {
          map.setConfigProperty('basemap', 'lightPreset', computeLightPreset());
          map.setConfigProperty('basemap', 'show3dObjects', true);
          map.setConfigProperty('basemap', 'showPointOfInterestLabels', true);
          map.setConfigProperty('basemap', 'showTransitLabels', false);
        }
      } catch (e) {
        // Non-fatal — style doesn't support these knobs.
      }

      // Pre-create the route source + layer once. Coordinates get patched in
      // by the effect below, so we don't have to add/remove a layer on every
      // driver-location update.
      if (!map.getSource('trip-route')) {
        map.addSource('trip-route', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        });
        map.addLayer({
          id: 'trip-route-casing',
          type: 'line',
          source: 'trip-route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#0047FF',
            'line-opacity': 0.35,
            'line-width': 8,
          },
        });
        map.addLayer({
          id: 'trip-route-line',
          type: 'line',
          source: 'trip-route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': '#4D88FF',
            'line-width': 4,
          },
        });
      }

      setMapReady(true);
    });

    // Cleanup: destroy the map fully on unmount so we don't leak WebGL context.
    return () => {
      // Cancel any in-flight driver marker easing tween.
      if (driverAnimRef.current) {
        cancelAnimationFrame(driverAnimRef.current);
        driverAnimRef.current = null;
      }
      // Remove all markers first (they hold DOM references)
      Object.values(markersRef.current).forEach((m) => m?.remove && m.remove());
      markersRef.current = {};
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      routeLoadedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- Markers: pickup / dropoff / customer (all static) --------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    // Google-Maps-style A/B badges so start vs finish is unambiguous.
    // Pickup = green "A" (start), Dropoff = dark "B" with checkered-flag
    // caption below it (finish). Customer pin stays with the person emoji.
    const pin = (loc, color, label, key, opts = {}) => {
      const lngLat = toLngLat(loc);
      if (!lngLat) {
        if (markersRef.current[key]) {
          markersRef.current[key].remove();
          delete markersRef.current[key];
        }
        return;
      }
      if (markersRef.current[key]) {
        markersRef.current[key].setLngLat(lngLat);
        return;
      }
      const wrap = document.createElement('div');
      wrap.style.cssText = `
        display: flex; flex-direction: column; align-items: center;
        transform: translateY(-6px); pointer-events: none;
      `;
      const badge = document.createElement('div');
      badge.style.cssText = `
        width: 32px; height: 32px; border-radius: 50%;
        background: ${color}; color: white;
        font-size: ${opts.fontSize || '14px'};
        font-weight: 800; letter-spacing: 0.5px;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 3px 10px rgba(0,0,0,0.4);
        border: 2px solid rgba(255,255,255,0.95);
      `;
      badge.textContent = label;
      wrap.appendChild(badge);
      if (opts.caption) {
        const cap = document.createElement('div');
        cap.style.cssText = `
          margin-top: 2px; padding: 1px 6px; border-radius: 999px;
          background: rgba(0,0,0,0.75); color: white;
          font-size: 9px; font-weight: 700; letter-spacing: 0.5px;
          text-transform: uppercase; white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.15);
        `;
        cap.textContent = opts.caption;
        wrap.appendChild(cap);
      }
      markersRef.current[key] = new mapboxgl.Marker({ element: wrap, anchor: 'bottom' })
        .setLngLat(lngLat)
        .addTo(map);
    };
    pin(pickupLocation, '#00c853', 'A', 'pickup', { caption: 'Pickup' });
    pin(dropoffLocation, '#111', 'B', 'dropoff', { caption: '🏁 Dropoff' });
    pin(customerLocation, '#ff9100', '🧑', 'customer', { fontSize: '16px' });
  }, [pickupLocation, dropoffLocation, customerLocation, mapReady]);

  // -------- Driver marker: smooth ease-to instead of manual lerp --------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    const lngLat = toLngLat(driverLocation);
    if (!lngLat) {
      if (markersRef.current.driver) {
        markersRef.current.driver.remove();
        delete markersRef.current.driver;
      }
      return;
    }
    if (!markersRef.current.driver) {
      const el = document.createElement('div');
      el.style.cssText = `
        width: 40px; height: 40px; border-radius: 50%;
        background: #000; color: white; font-size: 20px;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 0 0 4px rgba(255,255,255,0.9), 0 4px 14px rgba(0,0,0,0.5);
        border: 2px solid #4D88FF;
      `;
      el.textContent = '🚗';
      markersRef.current.driver = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(lngLat)
        .addTo(map);
    } else {
      // Ease the marker to the new position — Mapbox handles the animation
      // frame loop, so it's smoother + cheaper than the manual RAF loop we
      // had on the Leaflet path.
      // Cancel any prior in-flight tween so two RAF loops don't race and
      // jitter the marker when a new driverLocation arrives mid-animation.
      if (driverAnimRef.current) {
        cancelAnimationFrame(driverAnimRef.current);
        driverAnimRef.current = null;
      }
      const cur = markersRef.current.driver.getLngLat();
      const dx = Math.abs(cur.lng - lngLat[0]);
      const dy = Math.abs(cur.lat - lngLat[1]);
      // If the jump is huge (>500m-ish) just snap to avoid slow animations.
      if (dx > 0.005 || dy > 0.005) {
        markersRef.current.driver.setLngLat(lngLat);
      } else {
        // Small step → animate over ~500ms via CSS transform interpolation.
        // Mapbox markers don't expose a built-in animator, so we approximate
        // with requestAnimationFrame over a short duration.
        const start = performance.now();
        const from = [cur.lng, cur.lat];
        const to = lngLat;
        const durMs = 500;
        const step = (now) => {
          const t = Math.min(1, (now - start) / durMs);
          const cx = from[0] + (to[0] - from[0]) * t;
          const cy = from[1] + (to[1] - from[1]) * t;
          markersRef.current.driver?.setLngLat([cx, cy]);
          if (t < 1) {
            driverAnimRef.current = requestAnimationFrame(step);
          } else {
            driverAnimRef.current = null;
          }
        };
        driverAnimRef.current = requestAnimationFrame(step);
      }
    }
  }, [driverLocation, mapReady]);

  // -------- Fit bounds so the whole trip is visible --------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    const points = [
      toLngLat(pickupLocation),
      toLngLat(dropoffLocation),
      toLngLat(driverLocation),
      toLngLat(customerLocation),
    ].filter(Boolean);
    if (points.length === 0) return;
    if (points.length === 1) {
      map.easeTo({ center: points[0], zoom: 15, duration: 700 });
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, {
      padding: { top: 60, bottom: 100, left: 40, right: 40 },
      maxZoom: 15,
      duration: 700,
    });
  }, [pickupLocation, dropoffLocation, driverLocation, customerLocation, mapReady]);

  // -------- Traffic-aware route polyline --------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !showRoute) return;

    const src = map.getSource('trip-route');
    if (!src) return;

    // Anchor waypoints: driver → pickup → dropoff (fallback: pickup → dropoff).
    const start = toLngLat(driverLocation) || toLngLat(pickupLocation);
    const mid = toLngLat(driverLocation) ? toLngLat(pickupLocation) : null;
    const end = toLngLat(dropoffLocation);
    if (!start || !end) {
      src.setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const waypoints = mid ? [start, mid, end] : [start, end];
    const coords = waypoints.map(([lng, lat]) => `${lng},${lat}`).join(';');

    // Client-side debounce: skip the fetch when neither pickup/dropoff nor
    // a meaningful driver movement changed since our last successful fetch.
    // "Meaningful" = >100m along either lat/lng (~0.001 deg is roughly 110m
    // at mid-latitudes) OR >90s elapsed (traffic freshness).
    const stableKey = [
      pickupLocation?.lat, pickupLocation?.lng,
      dropoffLocation?.lat, dropoffLocation?.lng,
    ].join(',');
    const now = Date.now();
    const anchor = lastRouteFetchRef.current.driverAnchor;
    const driverMovedFar = driverLocation && anchor
      ? (Math.abs(driverLocation.lat - anchor.lat) > 0.001 || Math.abs(driverLocation.lng - anchor.lng) > 0.001)
      : true;
    const cacheStale = now - lastRouteFetchRef.current.at > 90 * 1000;
    const waypointsChanged = stableKey !== lastRouteFetchRef.current.key;
    if (!waypointsChanged && !driverMovedFar && !cacheStale) {
      return; // Serve last-drawn polyline — nothing worth re-fetching.
    }

    const backend = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backend}/api/geo/directions?coords=${encodeURIComponent(coords)}`);
        if (!res.ok) return;
        const data = await res.json();
        const line = data?.routes?.[0]?.geometry;
        if (!line || cancelled) return;
        src.setData({ type: 'Feature', geometry: line, properties: {} });
        lastRouteFetchRef.current = {
          key: stableKey,
          at: now,
          driverAnchor: driverLocation ? { lat: driverLocation.lat, lng: driverLocation.lng } : null,
        };
      } catch {
        // Silent — route line is a nice-to-have. Fallback below draws a
        // straight line so the user still sees SOMETHING connecting the dots.
        if (!cancelled) {
          src.setData({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: waypoints },
            properties: { fallback: true },
          });
        }
      }
    })();

    return () => { cancelled = true; };
    // Re-fetch when any waypoint changes — but not on every driver ping
    // (that would eat directions API quota); the debounce above is what
    // actually protects the quota.
  }, [
    // Serialize waypoints so React does a value-compare instead of ref-compare.
    driverLocation?.lat, driverLocation?.lng,
    pickupLocation?.lat, pickupLocation?.lng,
    dropoffLocation?.lat, dropoffLocation?.lng,
    showRoute, mapReady,
  ]);

  // -------- Container resize handling (iOS toolbar collapse, etc.) --------
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    const kick = () => { try { map.resize(); } catch (e) { /* ignore */ } };
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
      ro = new ResizeObserver(kick);
      ro.observe(mapRef.current);
    }
    window.addEventListener('resize', kick);
    window.addEventListener('orientationchange', kick);
    kick();
    setTimeout(kick, 400);
    setTimeout(kick, 1200);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', kick);
      window.removeEventListener('orientationchange', kick);
    };
  }, [mapReady]);

  if (tokenMissing) {
    return (
      <div
        style={{ height, width: '100%' }}
        className="rounded-xl bg-zinc-900 border border-white/10 flex items-center justify-center p-4 text-center"
        data-testid="live-map-token-missing"
      >
        <div>
          <p className="text-xs text-zinc-400 mb-1 uppercase tracking-wider">Map disabled</p>
          <p className="text-sm text-white">Mapbox token not configured</p>
          <p className="text-[11px] text-zinc-500 mt-1">Set REACT_APP_MAPBOX_TOKEN in frontend/.env</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ height, width: '100%', position: 'relative' }}
      className="rounded-xl overflow-hidden bg-zinc-900"
    >
      <div ref={mapRef} style={{ height: '100%', width: '100%', background: '#0a0a0a' }} data-testid="live-map" />
      {mapError && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-zinc-950/95 p-5 text-center"
          data-testid="live-map-error"
        >
          <div className="max-w-sm">
            <p className="text-xs text-rose-300 mb-1 uppercase tracking-wider font-bold">Map didn&apos;t load</p>
            <p className="text-sm text-white leading-snug">{mapError}</p>
            <p className="text-[11px] text-zinc-500 mt-3">
              Fix: add <code className="bg-white/10 px-1 rounded">REACT_APP_MAPBOX_TOKEN</code> to your deployment&apos;s env vars, or whitelist this domain in your Mapbox dashboard, then redeploy.
            </p>
          </div>
        </div>
      )}
      {!mapReady && !mapError && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 pointer-events-none">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}
      {/* Compact legend — matches the Leaflet version so the tracking UI
          doesn't feel different depending on which renderer is active. */}
      {mapReady && (pickupLocation || dropoffLocation || driverLocation) && (
        <div className="absolute top-2 left-2 flex flex-col gap-1 pointer-events-none">
          {driverLocation && (
            <div className="flex items-center gap-1 bg-black/70 backdrop-blur px-2 py-1 rounded-md text-white text-[10px]">
              <Navigation className="w-3 h-3 text-[#4D88FF]" /> Driver
            </div>
          )}
          {pickupLocation && (
            <div className="flex items-center gap-1 bg-black/70 backdrop-blur px-2 py-1 rounded-md text-white text-[10px]">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 text-[8px] font-bold text-white">A</span> Pickup
            </div>
          )}
          {dropoffLocation && (
            <div className="flex items-center gap-1 bg-black/70 backdrop-blur px-2 py-1 rounded-md text-white text-[10px]">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-black border border-white/40 text-[8px] font-bold text-white">B</span> Dropoff 🏁
            </div>
          )}
        </div>
      )}
    </div>
  );
}
